"use client";

import { IoClose } from "react-icons/io5";
import { RiCloseFill } from "react-icons/ri";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

export default function CartSidebar({ open, onClose, cart, onDelete, onOrderSuccess }) {
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [loadingCoupon, setLoadingCoupon] = useState(false);
  const [loadingCheckout, setLoadingCheckout] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");

  // ✅ Pending session conflict state
  const [pendingSessionWarning, setPendingSessionWarning] = useState(false);
  const pendingCouponCodeRef = useRef(null);

  const prevItemCountRef = useRef(cart?.items?.length ?? 0);
  const navigate = useNavigate();

  // Reset coupon when cart item count changes
  useEffect(() => {
    const currentCount = cart?.items?.length ?? 0;

    if (currentCount !== prevItemCountRef.current) {
      handleRemoveCoupon();
      prevItemCountRef.current = currentCount;
    }
  }, [cart?.items?.length]);

  const handleRemoveCoupon = () => {
    setCouponInput("");
    setAppliedCoupon(null);
    setCouponError("");
    setPendingSessionWarning(false);
    pendingCouponCodeRef.current = null;
  };

  const handleApplyCoupon = async (abandonPendingSession = false) => {
    const code = abandonPendingSession
      ? pendingCouponCodeRef.current
      : couponInput.trim().toUpperCase();

    if (!code) return;

    setLoadingCoupon(true);
    setCouponError("");
    setPendingSessionWarning(false);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cart/coupon/apply`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-sh-auth": localStorage.getItem("token"),
          },
          body: JSON.stringify({
            code,
            totalItems: cart?.items?.length,
            cartAmount: cart?.totalAmount,
            abandonPendingSession, // ✅ Send confirmation flag
          }),
        }
      );

      const data = await res.json();

      if (data?.code === 200) {
        setAppliedCoupon({
          code,
          discountAmount: data.data.discountAmount,
          finalAmount: data.data.finalAmount,
        });
        setCouponError("");
        setPendingSessionWarning(false);
        pendingCouponCodeRef.current = null;
      } else if (data?.code === 409 && data?.pendingSession) {
        // ✅ Show abandon confirmation inline — don't show generic error
        pendingCouponCodeRef.current = code;
        setPendingSessionWarning(true);
        setCouponError("");
      } else {
        setAppliedCoupon(null);
        setCouponError(data?.message || "Invalid coupon");
      }
    } catch (err) {
      setCouponError("Something went wrong. Please try again.");
    } finally {
      setLoadingCoupon(false);
    }
  };

  const handleCheckout = async () => {
    if (loadingCheckout) return;
    setLoadingCheckout(true);
    setCheckoutError("");

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cart/checkout`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-sh-auth": localStorage.getItem("token"),
          },
          body: JSON.stringify({
            couponCode: appliedCoupon?.code ?? null,
            totalItems: cart?.items?.length,
            totalAmount: cart?.totalAmount,
            cartItemIds: cart?.items?.map(item => item._id).join(","),
          }),
        }
      );

      const data = await res.json();

      if (data?.code === 200) {
        if (data.data.free) {
          window.gtag?.("event", "purchase", {
            transaction_id: `free_${appliedCoupon?.code}_${Date.now()}`,
            value: 0,
            currency: "USD",
            items:
              cart?.items?.map((item) => ({
                item_id: item._id,
                item_name: item?.eventId?.name,
                quantity: 1,
                price: 0,
              })) || [],
          });
          onClose();                     // ✅ Close the sidebar immediately
          onOrderSuccess?.();
          navigate("/dashboard/cart?status=success");
        } else {
          window.location.href = data.data.checkoutUrl;
        }
      }  else if (data?.code === 409 && data?.pendingSession) {
        // ✅ Show abandon confirmation inline — don't show generic error
        pendingCouponCodeRef.current = data?.code;
        setPendingSessionWarning(true);
        setCouponError(""); 
      } else {
        setCheckoutError(data?.message || "Checkout failed. Please try again.");
        if (data?.couponInvalid) {
          handleRemoveCoupon();
        }
      }
    } catch (error) {
      setCheckoutError("Something went wrong. Please try again.");
    } finally {
      setLoadingCheckout(false);
    }
  };

  const subtotal = cart?.totalAmount ?? 0;
  const discount = appliedCoupon?.discountAmount ?? 0;
  const finalAmount = appliedCoupon?.finalAmount ?? subtotal;
  const couponLocked = appliedCoupon !== null;

  return (
    <div>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Sidebar */}
      <div
        className={`fixed top-0 right-0 h-full w-[90%] sm:w-[400px] bg-white shadow-xl z-50 
        transition-transform duration-300 p-5 flex flex-col 
        ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Your Cart</h2>
          <button onClick={onClose}>
            <IoClose size={28} />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto space-y-4">
          {cart?.items?.length === 0 && (
            <p className="text-gray-500 text-center mt-10">Your cart is empty.</p>
          )}

          {cart?.items?.map((item) => (
            <div
              key={item._id}
              className="flex gap-3 border rounded-lg p-3 shadow-sm relative"
            >
              <img
                src={item.cardId?.thumbnail}
                alt="card"
                className="w-20 h-20 object-cover rounded-md border"
              />
              <div className="flex flex-col justify-between flex-1">
                <p className="font-semibold">{item?.eventId?.name}</p>
                <p className="text-sm text-gray-600">
                  Amount: ${item.amount?.toFixed(2)}
                </p>
              </div>
              <button
                onClick={() => onDelete(item._id)}
                className="absolute top-2 right-2 text-red-500 hover:text-red-700"
              >
                <RiCloseFill size={18} />
              </button>
            </div>
          ))}
        </div>

        {/* Footer */}
        {cart?.items?.length > 0 && (
          <div className="mt-4 border-t pt-4 space-y-4">
            {/* Coupon */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Have a coupon?</p>

              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Enter coupon code"
                  value={couponLocked ? appliedCoupon.code : couponInput}
                  onChange={(e) =>
                    !couponLocked && setCouponInput(e.target.value.toUpperCase())
                  }
                  disabled={couponLocked}
                  className={`flex-1 border px-3 py-2 rounded-md text-sm uppercase tracking-wider transition-colors
                    ${
                      couponLocked
                        ? "bg-green-50 border-green-400 text-green-700 cursor-not-allowed"
                        : "border-gray-300"
                    }`}
                />
                <button
                  onClick={
                    couponLocked ? handleRemoveCoupon : () => handleApplyCoupon(false)
                  }
                  disabled={loadingCoupon || (!couponLocked && !couponInput.trim())}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap
                    ${
                      couponLocked
                        ? "bg-red-100 text-red-600 hover:bg-red-200"
                        : "bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-40"
                    }`}
                >
                  {couponLocked ? "Remove" : loadingCoupon ? "Applying..." : "Apply"}
                </button>
              </div>

              {/* ✅ Pending session conflict warning with confirm/cancel */}
              {pendingSessionWarning && (
                <div className="bg-amber-50 border border-amber-300 rounded-md p-3 space-y-2">
                  <p className="text-amber-800 text-xs font-medium">
                    ⚠ You have an active checkout session using this coupon. Continuing will
                    abandon that session and allow you to start a new one.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleApplyCoupon(true)}
                      disabled={loadingCoupon}
                      className="flex-1 bg-amber-600 text-white text-xs py-1.5 rounded-md hover:bg-amber-700 disabled:opacity-50"
                    >
                      {loadingCoupon ? "Processing..." : "Abandon & Continue"}
                    </button>
                    <button
                      onClick={() => {
                        setPendingSessionWarning(false);
                        pendingCouponCodeRef.current = null;
                      }}
                      className="flex-1 border border-amber-400 text-amber-700 text-xs py-1.5 rounded-md hover:bg-amber-100"
                    >
                      Keep Existing Session
                    </button>
                  </div>
                </div>
              )}

              {/* Coupon error */}
              {couponError && (
                <p className="text-red-500 text-xs">⚠ {couponError}</p>
              )}

              {/* Coupon success */}
              {couponLocked && discount > 0 && (
                <p className="text-green-600 text-xs">
                  ✓ <span className="font-semibold">{appliedCoupon.code}</span> applied — you
                  saved ${discount.toFixed(2)}
                </p>
              )}

              {/* 100% off */}
              {couponLocked && finalAmount === 0 && (
                <p className="text-green-700 text-xs font-semibold">
                  🎉 Your order is on us!
                </p>
              )}
            </div>

            {/* Price Breakdown */}
            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-gray-500">
                <span>Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>

              {discount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount ({appliedCoupon.code})</span>
                  <span>− ${discount.toFixed(2)}</span>
                </div>
              )}

              <div className="flex justify-between text-base font-semibold border-t pt-2">
                <span>Total</span>
                <span>${finalAmount.toFixed(2)}</span>
              </div>
            </div>

            {/* Checkout error */}
            {checkoutError && (
              <p className="text-red-500 text-xs text-center">⚠ {checkoutError}</p>
            )}

            {/* Checkout Button */}
            <button
              onClick={handleCheckout}
              disabled={loadingCheckout}
              className="w-full bg-black text-white py-3 rounded-md text-sm font-medium 
                hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                flex items-center justify-center gap-2"
            >
              {loadingCheckout ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Processing...
                </>
              ) : finalAmount === 0 ? (
                "Claim Your Order"
              ) : (
                `Pay $${finalAmount.toFixed(2)}`
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}